import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, type ExcavatorHazard } from '../../../hazards/HazardDefinition';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { hazardSocket, mirroredHazard, mirroredVariant, sidesWithout } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type FixedObstaclePlacement, type ObstacleVariant, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_10 – excavator area 40 m (design/01_segments/SEG_10_Excavator). A crawler excavator works
 * behind the fence (moving hazard, see hazards/ExcavatorModel): after a warning it slews its arm
 * over the outer lane on its side and dumps a rubble heap there (z 22) – while the bucket hangs
 * over the lane, the lane is blocked; the heap stays (jump). Rubble, concrete blocks and
 * barriers lie in the other lanes; bottles arc over the heaps.
 *
 *   LEFT_RUBBLE    excavator left (lane 0) · R10: L1 rubble (jump)  · R33: L2 concrete block (jump)
 *   LEFT_BARRIERS  excavator left (lane 0) · R10: L2 barrier (jump) · R33: L1 container (lane change)
 *   RIGHT_*        mirrored, excavator right (lane 2)
 */
export const EXCAVATOR_AREA = {
  length: 40,
  /** Slew centre beside the track – its tail swing stays behind the fence. */
  machine: { x: -7.8, z: 22 },
  dumpZ: 22,
  rows: { R10: 10, R33: 33 },
} as const;

const W = WORLD_CONFIG;
const GROUND_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;
const SOCKET_LEFT = 'SOCKET_EVT_EXCAVATOR_L';
const SOCKET_RIGHT = 'SOCKET_EVT_EXCAVATOR_R';

const EXCAVATOR_LEFT: ExcavatorHazard = {
  id: 'HAZ_EXCAVATOR_L',
  kind: HazardKind.Excavator,
  socketId: SOCKET_LEFT,
  machine: EXCAVATOR_AREA.machine,
  lane: 0,
  z: EXCAVATOR_AREA.dumpZ,
};
const EXCAVATOR_RIGHT: ExcavatorHazard = { ...mirroredHazard(W, 'HAZ_EXCAVATOR_R', EXCAVATOR_LEFT), socketId: SOCKET_RIGHT };

const place = (row: keyof typeof EXCAVATOR_AREA.rows, lane: number, obstacleId: string): FixedObstaclePlacement => ({
  spawnPointId: `SOCKET_OBS_${row}_L${lane}`,
  obstacleId,
});

const LEFT_RUBBLE: ObstacleVariant = {
  id: 'LEFT_RUBBLE',
  weight: 1,
  placements: [place('R10', 1, 'OBS_RUBBLE_HEAP_A'), place('R33', 2, 'OBS_CONCRETE_BLOCK_A')],
  hazards: [EXCAVATOR_LEFT.id],
};

const LEFT_BARRIERS: ObstacleVariant = {
  id: 'LEFT_BARRIERS',
  weight: 1,
  placements: [place('R10', 2, 'OBS_BARRIER_A'), place('R33', 1, 'OBS_CONTAINER_A')],
  hazards: [EXCAVATOR_LEFT.id],
};

// The machine and its digging spot stand where the second block's first bay would be (z 20–30, both
// sides – the variant picks the side); its tail swings to z 19.2, so the bay before it shrinks to a
// single ground prop. The idle arm reaches z ≈ 28 – the last bay (z 30–40) stays.
const SIDES = sidesWithout(EXCAVATOR_AREA.length, ['SOCKET_DECO_L_01_2', 'SOCKET_DECO_R_01_2'], ['SOCKET_DECO_L_02', 'SOCKET_DECO_R_02']);

export const SEG_EXCAVATOR_40M: SegmentDefinition = {
  id: 'SEG_EXCAVATOR_40M',
  kind: SegmentKind.Straight,
  displayName: 'Baggerbereich 40 m',
  length: EXCAVATOR_AREA.length,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [{ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } }],

  entry: entryConnector(),
  exits: [straightExit(EXCAVATOR_AREA.length)],
  lanes: standardLanes(W),

  obstacleSpawnPoints: Object.entries(EXCAVATOR_AREA.rows).flatMap(([row, z]) => obstacleRow(W, row, z, GROUND_TYPES)),
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    LEFT_RUBBLE,
    LEFT_BARRIERS,
    mirroredVariant(W, 'RIGHT_RUBBLE', LEFT_RUBBLE, [EXCAVATOR_RIGHT.id]),
    mirroredVariant(W, 'RIGHT_BARRIERS', LEFT_BARRIERS, [EXCAVATOR_RIGHT.id]),
  ],
  hazards: [EXCAVATOR_LEFT, EXCAVATOR_RIGHT],

  collectibleSpawnPoints: collectibleLanes(W, 1, EXCAVATOR_AREA.length - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  decorationSockets: SIDES.decorationSockets,
  advertisingSlots: SIDES.advertisingSlots,
  dynamicEventSockets: [
    ...SIDES.dynamicEventSockets,
    hazardSocket(SOCKET_LEFT, EXCAVATOR_LEFT.machine.x, EXCAVATOR_AREA.dumpZ, DynamicEventType.ExcavatorSwing),
    hazardSocket(SOCKET_RIGHT, EXCAVATOR_RIGHT.machine.x, EXCAVATOR_AREA.dumpZ, DynamicEventType.ExcavatorSwing),
  ],

  generation: {
    weight: 0.25,
    minDistance: 300,
    minSegmentsAfterJunction: 1,
    family: 'EXCAVATOR',
    tags: ['excavator', 'hazard', 'jump', 'lane-change'],
  },
};
